import { useId } from 'react';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  CartesianGrid,
  Tooltip,
  LineChart as RechartsLineChart,
  Line,
  Legend,
  Sankey,
  ScatterChart,
  Scatter
} from 'recharts';
import {
  formatChartValue,
  formatTooltipLabel,
  sankeyLabelPlacement,
  seriesColor,
  seriesLabel,
} from './chartStyle';

const AXIS_FONT = 'inherit';

const ChartTooltipContent = ({ active, payload, label, theme }) => {
  if (!active || !payload?.length || !theme) return null;
  const rows = payload.filter(entry => entry && entry.value != null && entry.type !== 'none');
  if (!rows.length) return null;
  const heading = label != null && label !== '' ? formatTooltipLabel(label) : null;

  return (
    <div
      style={{
        background: theme.tooltipBg,
        border: `1px solid ${theme.tooltipBorder}`,
        borderRadius: 10,
        boxShadow: theme.tooltipShadow,
        padding: '8px 10px',
        minWidth: 128,
      }}
    >
      {heading && (
        <div style={{ color: theme.tooltipMuted, fontSize: 11, fontWeight: 500, marginBottom: 4 }}>
          {heading}
        </div>
      )}
      {rows.map((entry, index) => (
        <div
          key={`${entry.dataKey ?? entry.name}-${index}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            lineHeight: '18px',
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: entry.color || entry.fill || entry.stroke || theme.axis,
              flex: 'none',
            }}
          />
          <span style={{ color: theme.tooltipMuted, fontWeight: 500 }}>
            {seriesLabel(entry.name)}
          </span>
          <span
            style={{
              marginLeft: 'auto',
              paddingLeft: 12,
              color: theme.tooltipText,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatChartValue(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const ThemedTooltip = ({ theme, cursor }) => (
  <Tooltip
    cursor={cursor}
    content={<ChartTooltipContent theme={theme} />}
    wrapperStyle={{ outline: 'none', zIndex: 20 }}
  />
);

const ChartLegend = ({ onClick, theme }) => (
  <Legend
    verticalAlign="top"
    align="left"
    onClick={onClick}
    iconType="circle"
    iconSize={8}
    formatter={(value) => seriesLabel(value)}
    wrapperStyle={{
      fontSize: 12,
      fontWeight: 500,
      fontFamily: AXIS_FONT,
      color: theme.tick,
      lineHeight: '16px',
      paddingBottom: 8,
      cursor: onClick ? 'pointer' : 'default',
    }}
  />
);

const ChartGrid = ({ theme }) => (
  <CartesianGrid vertical={false} stroke={theme.grid} strokeWidth={1} />
);

const SankeyLinkShape = ({
  sourceX,
  targetX,
  sourceY,
  targetY,
  sourceControlX,
  targetControlX,
  linkWidth,
  index,
  payload,
  nodes,
  gradientPrefix,
}) => {
  const sourceName = payload?.source?.name;
  const targetName = payload?.target?.name;
  const sourceIndex = nodes.findIndex(node => node.name === sourceName);
  const targetIndex = nodes.findIndex(node => node.name === targetName);
  const sourceColor = seriesColor(sourceIndex >= 0 ? sourceIndex : 0);
  const targetColor = seriesColor(targetIndex >= 0 ? targetIndex : 0);
  const gradientId = `${gradientPrefix}-${index}`;

  return (
    <g>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={sourceColor} />
          <stop offset="100%" stopColor={targetColor} />
        </linearGradient>
      </defs>
      <path
        d={`M${sourceX},${sourceY}C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth={Math.max(linkWidth, 1)}
        strokeOpacity={0.38}
      />
    </g>
  );
};

const SankeyNodeWithLabel = ({ x, y, width, height, index, payload, theme, paletteNodes = [] }) => {
  const nodeName = payload?.name || `Node ${index}`;
  const label = nodeName.length > 22 ? `${nodeName.slice(0, 21)}…` : nodeName;
  const placement = sankeyLabelPlacement(payload);
  const namedIndex = paletteNodes.findIndex(node => node.name === nodeName);
  const safeW = Math.max(0.5, width || 0);
  const safeH = Math.max(0.5, height || 0);
  const labelProps = placement === 'above'
    ? { x: x + safeW / 2, y: y - 4, textAnchor: 'middle', dominantBaseline: 'text-after-edge' }
    : placement === 'left'
      ? { x: x - 8, y: y + safeH / 2, textAnchor: 'end', dominantBaseline: 'middle' }
      : { x: x + safeW + 8, y: y + safeH / 2, textAnchor: 'start', dominantBaseline: 'middle' };

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={safeW}
        height={safeH}
        fill={seriesColor(namedIndex >= 0 ? namedIndex : index)}
        rx={2}
      />
      <text
        {...labelProps}
        fontSize={12}
        fontWeight={500}
        fontFamily={AXIS_FONT}
        fill={theme.sankeyLabel}
        stroke={placement === 'above' ? theme.surface : 'none'}
        strokeWidth={placement === 'above' ? 3 : 0}
        paintOrder="stroke"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {label}
      </text>
    </g>
  );
};

export const BarTemplate = ({ data, series, xAxis, yAxis, hiddenSeries, handleLegendClick, theme, margin }) => (
  <BarChart data={data} margin={margin} barGap={4} barCategoryGap="22%">
    <ChartGrid theme={theme} />
    {xAxis}
    {yAxis}
    <ThemedTooltip theme={theme} cursor={{ fill: theme.cursor }} />
    {series.length > 1 && <ChartLegend onClick={handleLegendClick} theme={theme} />}
    {series.map((s, i) => (
      <Bar
        key={s}
        dataKey={s}
        fill={seriesColor(i)}
        maxBarSize={64}
        radius={[4, 4, 0, 0]}
        hide={hiddenSeries.has(s)}
        animationDuration={450}
      />
    ))}
  </BarChart>
);

export const LineTemplate = ({ data, series, xAxis, yAxis, hiddenSeries, handleLegendClick, theme, margin }) => {
  const showDots = Array.isArray(data) && data.length > 0 && data.length <= 18;

  return (
    <RechartsLineChart data={data} margin={margin}>
      <ChartGrid theme={theme} />
      {xAxis}
      {yAxis}
      <ThemedTooltip theme={theme} cursor={{ stroke: theme.cursorLine, strokeWidth: 1 }} />
      {series.length > 1 && <ChartLegend onClick={handleLegendClick} theme={theme} />}
      {series.map((s, i) => {
        const color = seriesColor(i);
        return (
          <Line
            key={s}
            type="monotoneX"
            dataKey={s}
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            dot={showDots ? { r: 3, strokeWidth: 2, stroke: color, fill: theme.dotFill } : false}
            activeDot={{ r: 5, strokeWidth: 2, stroke: theme.dotFill, fill: color }}
            hide={hiddenSeries.has(s)}
            animationDuration={450}
          />
        );
      })}
    </RechartsLineChart>
  );
};

export const ScatterTemplate = ({ data, series, xAxis, yAxis, hiddenSeries, handleLegendClick, spec, scatterSeriesData, theme, margin }) => (
  <ScatterChart data={data} margin={margin}>
    <ChartGrid theme={theme} />
    {xAxis}
    {yAxis}
    <ThemedTooltip theme={theme} cursor={{ strokeDasharray: '3 3', stroke: theme.cursorLine }} />
    {series.length > 1 && <ChartLegend onClick={handleLegendClick} theme={theme} />}
    {series.map((s, i) => {
      const color = seriesColor(i);
      const filteredData = spec?.grouping
        ? (scatterSeriesData?.[s] || [])
        : data;
      const scatterDataKey = spec?.grouping ? spec?.y : s;

      return (
        <Scatter
          key={s}
          data={filteredData}
          dataKey={scatterDataKey}
          fill={color}
          stroke="none"
          fillOpacity={0.75}
          size={72}
          legendType="circle"
          hide={hiddenSeries.has(s)}
          animationDuration={450}
        />
      );
    })}
  </ScatterChart>
);

export const HistogramTemplate = ({ data, series, xAxis, yAxis, hiddenSeries, handleLegendClick, theme, margin }) => {
  const isComparison = series.length > 1;

  if (isComparison) {
    return (
      <AreaChart data={data} margin={margin}>
        <ChartGrid theme={theme} />
        {xAxis}
        {yAxis}
        <ThemedTooltip theme={theme} cursor={{ stroke: theme.cursorLine, strokeWidth: 1 }} />
        <ChartLegend onClick={handleLegendClick} theme={theme} />
        {series.map((s, i) => (
          <Area
            key={s}
            type="monotoneX"
            dataKey={s}
            stroke={seriesColor(i)}
            fill={seriesColor(i)}
            fillOpacity={0.22}
            strokeWidth={2}
            hide={hiddenSeries.has(s)}
            animationDuration={450}
          />
        ))}
      </AreaChart>
    );
  }

  return (
    <BarChart data={data} margin={margin} barGap={0} barCategoryGap={0}>
      <ChartGrid theme={theme} />
      {xAxis}
      {yAxis}
      <ThemedTooltip theme={theme} cursor={{ fill: theme.cursor }} />
      {series.map((s, i) => (
        <Bar
          key={s}
          dataKey={s}
          fill={seriesColor(i)}
          stroke={theme.surface}
          strokeWidth={1}
          radius={0}
          hide={hiddenSeries.has(s)}
          animationDuration={450}
        />
      ))}
    </BarChart>
  );
};

export const SankeyTemplate = ({ data, theme }) => {
  const gradientPrefix = useId().replace(/:/g, '');
  const nodes = data?.nodes || [];

  return (
    <Sankey
      data={data}
      nodeWidth={12}
      nodePadding={28}
      linkCurvature={0.5}
      margin={{ top: 22, right: 128, left: 128, bottom: 12 }}
      node={<SankeyNodeWithLabel theme={theme} paletteNodes={nodes} />}
      link={(linkProps) => (
        <SankeyLinkShape
          {...linkProps}
          nodes={nodes}
          gradientPrefix={gradientPrefix}
        />
      )}
      sort
      iterations={64}
      nameKey="name"
    >
      <ThemedTooltip theme={theme} cursor={false} />
    </Sankey>
  );
};
